import { useEffect, useRef } from 'react'
import {
  useWatch,
  type Control,
  type FieldPath,
  type FieldValues,
  type SetValueConfig,
  type UseFormSetValue
} from 'react-hook-form'
import type { PathValue } from 'react-hook-form'

function toDate0(v: unknown): Date | null {
  if (!v) return null
  const d = v instanceof Date ? v : new Date(v as string | number)
  if (Number.isNaN(d.getTime())) return null
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}

export function useFollowDeliveryByShipping<
  TFieldValues extends FieldValues,
  TShippingName extends FieldPath<TFieldValues>,
  TDeliveryName extends FieldPath<TFieldValues>
>(params: {
  control: Control<TFieldValues>
  setValue: UseFormSetValue<TFieldValues>
  shippingName: TShippingName
  deliveryName: TDeliveryName
  enabled?: boolean
  options?: SetValueConfig
}) {
  const {
    control,
    setValue,
    shippingName,
    deliveryName,
    enabled = true,
    options = { shouldDirty: true, shouldValidate: true }
  } = params

  const shippingWatch = useWatch({ control, name: shippingName })
  const deliveryWatch = useWatch({ control, name: deliveryName })

  const prevShippingRef = useRef<Date | null>(null)

  useEffect(() => {
    const s = toDate0(shippingWatch)
    const d = toDate0(deliveryWatch)

    if (!enabled) {
      prevShippingRef.current = s
      return
    }

    // 初回は保持だけ
    if (prevShippingRef.current === null) {
      prevShippingRef.current = s
      return
    }

    // 空なら追従しない
    if (!s || !d) {
      prevShippingRef.current = s
      return
    }

    // shipping 変化なし
    if (prevShippingRef.current.getTime() === s.getTime()) return

    const shiftMs = s.getTime() - prevShippingRef.current.getTime()
    const nextDelivery = new Date(d.getTime() + shiftMs)
    nextDelivery.setHours(0, 0, 0, 0)

    const nextValue = nextDelivery as PathValue<TFieldValues, TDeliveryName>
    setValue(deliveryName, nextValue, options)

    prevShippingRef.current = s
  }, [
    enabled,
    shippingWatch,
    deliveryWatch,
    shippingName,
    deliveryName,
    setValue,
    options
  ])
}
